import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import authReducer from './slices/authSlice';
import portalAuthReducer from './slices/portalAuthSlice';
import bookkeepingReducer from './slices/bookkeepingSlice';
import masterDataReducer from './slices/masterDataSlice';
import statisticsReducer from './slices/statisticsSlice';
import uiReducer from './slices/uiSlice';
import { invalidatePosHistoryCache } from '../utils/posHistoryCache';
import { invalidateCsoListCache } from '../utils/csoListCache';
import { invalidateJobsListCache } from '../utils/jobsListCache';
import { invalidateInventoryListCache } from '../utils/inventoryListCache';
import { invalidateInventoryOverviewStatsCache } from '../utils/inventoryOverviewStatsCache';

const authPersistConfig = {
  key: 'auth',
  storage,
};

const portalAuthPersistConfig = {
  key: 'portalAuth',
  storage,
};

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedPortalAuthReducer = persistReducer(portalAuthPersistConfig, portalAuthReducer);

// Root reducer that handles clearing all state
const rootReducer = (state, action) => {
  if (action.type === 'auth/clearAllState') {
    // Return a completely fresh state
    return {
      auth: authReducer(undefined, action),
      portalAuth: portalAuthReducer(undefined, action),
      bookkeeping: bookkeepingReducer(undefined, action),
      masterData: masterDataReducer(undefined, action),
      statistics: statisticsReducer(undefined, action),
      ui: uiReducer(undefined, action),
    };
  }
  
  return {
    auth: persistedAuthReducer(state?.auth, action),
    portalAuth: persistedPortalAuthReducer(state?.portalAuth, action),
    bookkeeping: bookkeepingReducer(state?.bookkeeping, action),
    masterData: masterDataReducer(state?.masterData, action),
    statistics: statisticsReducer(state?.statistics, action),
    ui: uiReducer(state?.ui, action),
  };
};

const sessionListCacheMiddleware = () => (next) => (action) => {
  if (action.type === 'auth/logout' || action.type === 'auth/clearAllState') {
    invalidatePosHistoryCache();
    invalidateCsoListCache();
    invalidateJobsListCache();
    invalidateInventoryListCache();
    invalidateInventoryOverviewStatsCache();
  }
  return next(action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/REGISTER'],
      },
    }).concat(sessionListCacheMiddleware),
});

export const persistor = persistStore(store); 